import { rem } from 'polished'
import { useCallback, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import Caption from 'LuxKit/Typography/Caption'
import RichTooltip from 'LuxKit/RichTooltip'
import TextButton from 'LuxKit/Button/TextButton'
import useIsomorphicLayoutEffect from 'hooks/useIsomorphicLayoutEffect'
import useResizeObserver from 'hooks/useResizeObserver'
import GalleryV2PhotoGrid from './GalleryV2PhotoGrid'
import { GALLERY_CONTENT_MAX_WIDTH } from './GalleryModalV2.types'
import type { GalleryCategory } from './GalleryModalV2.types'

const SectionRoot = styled.div`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  width: 100%;
  min-width: 0;
  max-width: ${rem(GALLERY_CONTENT_MAX_WIDTH)};
  margin: 0 auto;
`

const SectionTitleBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${rem(16)};
  padding-block: ${rem(6)};
  padding-inline: ${rem(20)};
`

const SectionTitleTooltip = styled(RichTooltip)`
  min-width: 0;
`

const SectionTitle = styled(Caption)`
  min-width: 0;
`

interface Props {
  category: GalleryCategory
  onImageClick: (images: Array<App.Image>, index: number) => void
  onViewRoom?: (categoryId: string) => void
}

function GalleryV2CategorySection(props: Props) {
  const { category, onImageClick, onViewRoom } = props
  const intl = useIntl()

  const titleRef = useRef<HTMLDivElement>(null)
  const [isTitleTruncated, setIsTitleTruncated] = useState(false)

  const updateTitleTruncation = useCallback(() => {
    const titleElement = titleRef.current
    setIsTitleTruncated(Boolean(titleElement && titleElement.scrollWidth > titleElement.clientWidth))
  }, [])

  useIsomorphicLayoutEffect(updateTitleTruncation, [category.label, updateTitleTruncation])
  useResizeObserver(titleRef, updateTitleTruncation)

  const handleViewRoom = useCallback(() => {
    onViewRoom?.(category.id)
  }, [category.id, onViewRoom])

  const showViewRoom = category.type === 'room' && !!onViewRoom

  return <SectionRoot>
    <SectionTitleBar>
      <SectionTitleTooltip
        disabled={!isTitleTruncated}
        portaled
        dismissible={false}
        placement="bottom-start"
        variant="dark"
        showOnHover
        description={category.label}
      >
        <SectionTitle ref={titleRef} variant="large" weight="bold" colour="neutral-one" align="start" wrap="truncate">
          {category.label}
        </SectionTitle>
      </SectionTitleTooltip>
      {showViewRoom && <TextButton kind="tertiary" size="medium" onClick={handleViewRoom}>
        {intl.formatMessage({
          id: 'hijiMiEnoGOPIiXbJaQE',
          defaultMessage: 'View room',
        })}
      </TextButton>}
    </SectionTitleBar>
    <GalleryV2PhotoGrid images={category.images} onImageClick={onImageClick} />
  </SectionRoot>
}

export default GalleryV2CategorySection
